import { useEffect, useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Provider } from "react-redux";
import { store, type AppDispatch } from "./store/store";
import { loadAvailableClinics, selectIsAuthenticated, login } from "./store/slices/authSlice";
import { ConfigContextProvider, configContext } from "./contexts/config";
import { UserContextProvider } from "./contexts/user";
import { QuickActionsProvider } from "./contexts/QuickActionsContext";
import GlobalModals from "./components/GlobalModals";
import authService from "./services/authService";
import Index from "./pages/Index";
import Dashboard from "./pages/Dashboard";
import Catalogue from "./pages/Catalogue";
import PatientsRedux from "./pages/Patients";
import Orders from "./pages/Orders";
import CreateOrder from "./pages/CreateOrder";
import Admin from "./pages/Admin";
import Users from "./pages/Users";
import Reports from "./pages/Reports";
import Notifications from "./pages/Notifications";
import Profile from "./pages/Profile";
import Billing from "./pages/Billing";
import NotFound from "./pages/NotFound";
import SignUp from "./pages/SignUp"
import OnboardingWizard from "./components/onboarding/OnboardingWizard";
import ProtectedRoute from "./components/ProtectedRoute";
import PublicRoute from "./components/PublicRoute";
const queryClient = new QueryClient();

// Component to handle HATEOAS initialization
const HATEOASInitializer = () => {
  const dispatch = useDispatch<AppDispatch>();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const config = useContext(configContext);
  const [initialized, setInitialized] = useState(false);
  const [clinicsLoaded, setClinicsLoaded] = useState(false);

  // Initialize auth service and restore session (runs once)
  useEffect(() => {
    if (initialized || !config.cognito) return;
    
    const storedToken = sessionStorage.getItem('vitl_access_token');
    if (storedToken) {
      // Setup user pool for token refresh to work - use config from API
      const poolConfig = {
        userPoolId: config.cognito.userPoolId,
        clientId: config.cognito.clientId,
        endpoint: import.meta.env.VITL_COGNITO_ENDPOINT
      };
      authService.setupUserPool(poolConfig);
      
      // Restore authentication state from stored token
      if (!isAuthenticated) {
        const userEmail = sessionStorage.getItem('vitl_user_email');
        dispatch(login({
          user: {
            id: 'restored-user',
            email: userEmail || 'user@example.com',
            name: userEmail?.split('@')[0] || 'User'
          }
        }));
      }
    }
    
    setInitialized(true);
  }, [dispatch, isAuthenticated, initialized, config]);

  // Load clinics when authenticated (but only once)
  useEffect(() => {
    if (isAuthenticated && !clinicsLoaded) {
      console.log('Loading clinics for authenticated user...');
      dispatch(loadAvailableClinics());
      setClinicsLoaded(true);
    }
  }, [dispatch, isAuthenticated, clinicsLoaded]);

  return null;
};

const App = () => (
  <Provider store={store}>
    <ConfigContextProvider>
      <UserContextProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <HATEOASInitializer />
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <QuickActionsProvider>
                <GlobalModals />
                <Routes>
                  {/* Public routes - redirect to dashboard if authenticated */}
                  <Route path="/" element={<PublicRoute><Index /></PublicRoute>} />
                  <Route path="/signup" element={<PublicRoute><SignUp /></PublicRoute>} />
                  <Route path="/onboarding" element={<PublicRoute><OnboardingWizard /></PublicRoute>} />
                  
                  {/* Protected routes - require authentication */}
                  <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                  <Route path="/catalogue" element={<ProtectedRoute><Catalogue /></ProtectedRoute>} />
                  <Route path="/patients" element={<ProtectedRoute><PatientsRedux /></ProtectedRoute>} />
                  <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
                  <Route path="/create-order" element={<ProtectedRoute><CreateOrder /></ProtectedRoute>} />
                  <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
                  <Route path="/users" element={<ProtectedRoute><Users /></ProtectedRoute>} />
                  <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
                  <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
                  <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                  <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
                  
                  {/* 404 page - no protection needed */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </QuickActionsProvider>
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </UserContextProvider>
    </ConfigContextProvider>
  </Provider>
);

export default App;
